import { useState, useEffect } from "react";
import {
  FolderOpen,
  Tag,
  Trash2,
  Edit,
  Plus,
  RefreshCw,
  X,
  AlertCircle
} from "lucide-react";
import { Button } from "@/components/admin/ui/Button";
import { Input } from "@/components/admin/ui/Input";
import { Select } from "@/components/admin/ui/Select";

interface Term {
  term_id: number;
  name: string;
  slug: string;
  description: string;
  parent: number;
  count: number;
}

export function Categories() {
  const [categories, setCategories] = useState<Term[]>([]);
  const [tags, setTags] = useState<Term[]>([]);
  const [loadingCats, setLoadingCats] = useState(false);
  const [loadingTags, setLoadingTags] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Category form state
  const [catName, setCatName] = useState("");
  const [catSlug, setCatSlug] = useState("");
  const [catParent, setCatParent] = useState(0);
  const [catDesc, setCatDesc] = useState("");
  const [editingCatId, setEditingCatId] = useState<number | null>(null);

  // Tag form state
  const [tagName, setTagName] = useState("");
  const [tagSlug, setTagSlug] = useState("");
  const [tagDesc, setTagDesc] = useState("");
  const [editingTagId, setEditingTagId] = useState<number | null>(null);

  const getAjaxConfig = () => {
    const ajaxUrl =
      (window as any).shLmsAdmin?.ajax_url ||
      (window as any).hubInstructor?.ajax_url ||
      (window as any).ajaxurl;
    const nonce =
      (window as any).shLmsAdmin?.nonce ||
      (window as any).hubInstructor?.nonce ||
      (window as any).chorchaAdmin?.nonce;
    return { ajaxUrl, nonce };
  };

  const fetchTerms = (taxonomy: "course_batch_category" | "course_class_tag") => {
    const { ajaxUrl, nonce } = getAjaxConfig();
    if (!ajaxUrl || !(window as any).jQuery) return;

    if (taxonomy === "course_batch_category") {
      setLoadingCats(true);
    } else {
      setLoadingTags(true);
    }

    (window as any).jQuery.ajax({
      url: ajaxUrl,
      method: "POST",
      data: {
        action: "chorcha_get_course_terms",
        nonce: nonce,
        taxonomy: taxonomy
      },
      success: (res: any) => {
        if (res.success && Array.isArray(res.data)) {
          if (taxonomy === "course_batch_category") {
            setCategories(res.data);
          } else {
            setTags(res.data);
          }
        } else {
          setErrorMsg(res.data || "Failed to load terms.");
        }
      },
      error: () => {
        setErrorMsg("Failed to connect to the server.");
      },
      complete: () => {
        if (taxonomy === "course_batch_category") {
          setLoadingCats(false);
        } else {
          setLoadingTags(false);
        }
      }
    });
  };

  useEffect(() => {
    fetchTerms("course_batch_category");
    fetchTerms("course_class_tag");
  }, []);

  const handleSaveTerm = (
    e: React.FormEvent,
    taxonomy: "course_batch_category" | "course_class_tag"
  ) => {
    e.preventDefault();
    const { ajaxUrl, nonce } = getAjaxConfig();
    if (!ajaxUrl || !(window as any).jQuery) return;

    const isCat = taxonomy === "course_batch_category";
    const name = isCat ? catName : tagName;
    const slug = isCat ? catSlug : tagSlug;
    const description = isCat ? catDesc : tagDesc;
    const parent = isCat ? catParent : 0;
    const term_id = isCat ? editingCatId : editingTagId;

    if (!name.trim()) return;

    (window as any).jQuery.ajax({
      url: ajaxUrl,
      method: "POST",
      data: {
        action: "chorcha_save_course_term",
        nonce: nonce,
        taxonomy: taxonomy,
        term_id: term_id || 0,
        name: name,
        slug: slug,
        description: description,
        parent: parent
      },
      success: (res: any) => {
        if (res.success) {
          // Clear form & refresh
          if (isCat) {
            setCatName("");
            setCatSlug("");
            setCatDesc("");
            setCatParent(0);
            setEditingCatId(null);
          } else {
            setTagName("");
            setTagSlug("");
            setTagDesc("");
            setEditingTagId(null);
          }
          fetchTerms(taxonomy);
        } else {
          setErrorMsg(res.data || "Failed to save term.");
        }
      },
      error: () => {
        setErrorMsg("Error occurred while saving term.");
      }
    });
  };

  const handleDeleteTerm = (
    termId: number,
    taxonomy: "course_batch_category" | "course_class_tag"
  ) => {
    if (!confirm("Are you sure you want to delete this term?")) return;
    const { ajaxUrl, nonce } = getAjaxConfig();
    if (!ajaxUrl || !(window as any).jQuery) return;

    (window as any).jQuery.ajax({
      url: ajaxUrl,
      method: "POST",
      data: {
        action: "chorcha_delete_course_term",
        nonce: nonce,
        taxonomy: taxonomy,
        term_id: termId
      },
      success: (res: any) => {
        if (res.success) {
          fetchTerms(taxonomy);
        } else {
          setErrorMsg(res.data || "Failed to delete term.");
        }
      },
      error: () => {
        setErrorMsg("Error occurred while deleting term.");
      }
    });
  };

  const startEditCat = (term: Term) => {
    setEditingCatId(term.term_id);
    setCatName(term.name);
    setCatSlug(term.slug);
    setCatParent(term.parent);
    setCatDesc(term.description);
  };

  const startEditTag = (term: Term) => {
    setEditingTagId(term.term_id);
    setTagName(term.name);
    setTagSlug(term.slug);
    setTagDesc(term.description);
  };

  // Helper to build hierarchical category tree labels
  const getParentOptions = () => {
    return categories
      .filter((c) => c.term_id !== editingCatId) // Prevent self-parenting
      .map((c) => ({
        value: String(c.term_id),
        label: c.name
      }));
  };

  return (
    <div className="space-y-8 max-w-7xl mx-auto pb-12">
      {/* Header section with minimal Apple style */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-[#E2E8F0] pb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#1A1F2C]">Categories & Tags</h1>
          <p className="text-sm text-[#5A6A85] mt-1">
            Manage your course taxonomies (batches/cohorts and educational levels)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              fetchTerms("course_batch_category");
              fetchTerms("course_class_tag");
            }}
            className="h-9 px-3 border-[#E2E8F0]"
          >
            <RefreshCw className="h-4 w-4 mr-1.5 text-[#5A6A85]" />
            Refresh
          </Button>
        </div>
      </div>

      {errorMsg && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <span className="text-sm font-medium">{errorMsg}</span>
          </div>
          <button onClick={() => setErrorMsg("")} className="text-red-500 hover:text-red-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Main Grid: Categories on Left, Tags on Right */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
        
        {/* ==================== LEFT COLUMN: CATEGORIES (course_batch_category) ==================== */}
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-2.5 pb-5 border-b border-[#F1F5F9]">
            <div className="p-2 bg-[#F4F2FF] rounded-lg">
              <FolderOpen className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[#1A1F2C]">Course Categories (Batches)</h2>
              <p className="text-xs text-[#5A6A85]">Hierarchical categorization for cohorts/batches</p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={(e) => handleSaveTerm(e, "course_batch_category")} className="mt-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Category Name *"
                placeholder="e.g. SSC 2027"
                value={catName}
                onChange={(e) => setCatName(e.target.value)}
                required
              />
              <Input
                label="Slug (URL Friendly)"
                placeholder="e.g. ssc-2027"
                value={catSlug}
                onChange={(e) => setCatSlug(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="w-full">
                <label className="block text-sm font-medium text-[#374151] mb-1.5">
                  Parent Category
                </label>
                <Select
                  value={String(catParent)}
                  onChange={(e) => setCatParent(Number(e.target.value))}
                  options={[
                    { value: "0", label: "None (Parent)" },
                    ...getParentOptions()
                  ]}
                  className="w-full"
                />
              </div>
              <Input
                label="Description"
                placeholder="Brief details about cohort..."
                value={catDesc}
                onChange={(e) => setCatDesc(e.target.value)}
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              {editingCatId && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEditingCatId(null);
                    setCatName("");
                    setCatSlug("");
                    setCatDesc("");
                    setCatParent(0);
                  }}
                >
                  Cancel
                </Button>
              )}
              <Button type="submit" size="sm" className="bg-[#ff0000] hover:bg-[#e00000] text-white">
                <Plus className="h-4 w-4 mr-1" />
                {editingCatId ? "Update Category" : "Add Category"}
              </Button>
            </div>
          </form>

          {/* Categories List */}
          <div className="mt-6 border-t border-[#F1F5F9] pt-6">
            <h3 className="text-sm font-semibold text-[#1A1F2C] mb-3">Existing Categories</h3>
            {loadingCats ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 text-indigo-500 animate-spin" />
              </div>
            ) : categories.length === 0 ? (
              <p className="text-sm text-[#5A6A85] text-center py-6">No categories added yet.</p>
            ) : (
              <div className="space-y-2 max-h-[350px] overflow-y-auto pr-1">
                {categories.map((cat) => (
                  <div
                    key={cat.term_id}
                    className="flex items-center justify-between p-3 rounded-xl border border-[#E2E8F0] hover:border-indigo-100 bg-[#FBFBFF] transition-all"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm text-[#1A1F2C] truncate">
                          {cat.name}
                        </span>
                        {cat.parent > 0 && (
                          <span className="text-[10px] text-indigo-500 bg-[#F4F2FF] px-1.5 py-0.5 rounded-full font-medium">
                            Sub
                          </span>
                        )}
                        {/* Apple-style Badge backgrounds */}
                        <span
                          style={{ backgroundColor: "#00C9A7", color: "#1A1F2C" }}
                          className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                        >
                          Mint Category
                        </span>
                      </div>
                      <p className="text-xs text-[#5A6A85] mt-1 truncate">
                        {cat.description || "No description."} (Slug: {cat.slug})
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 ml-4">
                      <span className="text-xs font-semibold text-[#5A6A85] bg-white border border-[#E2E8F0] px-2 py-0.5 rounded-md">
                        {cat.count} {cat.count === 1 ? "course" : "courses"}
                      </span>
                      <button
                        onClick={() => startEditCat(cat)}
                        className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors cursor-pointer"
                        title="Edit"
                      >
                        <Edit className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => handleDeleteTerm(cat.term_id, "course_batch_category")}
                        className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                        title="Delete"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ==================== RIGHT COLUMN: TAGS (course_class_tag) ==================== */}
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-2.5 pb-5 border-b border-[#F1F5F9]">
            <div className="p-2 bg-[#EEFDF7] rounded-lg">
              <Tag className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[#1A1F2C]">Course Tags (Class Tags)</h2>
              <p className="text-xs text-[#5A6A85]">Non-hierarchical tags for educational levels</p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={(e) => handleSaveTerm(e, "course_class_tag")} className="mt-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Tag Name *"
                placeholder="e.g. Admission"
                value={tagName}
                onChange={(e) => setTagName(e.target.value)}
                required
              />
              <Input
                label="Slug (URL Friendly)"
                placeholder="e.g. admission"
                value={tagSlug}
                onChange={(e) => setTagSlug(e.target.value)}
              />
            </div>

            <Input
              label="Description"
              placeholder="Brief details about class tag..."
              value={tagDesc}
              onChange={(e) => setTagDesc(e.target.value)}
            />

            <div className="flex items-center justify-end gap-2 pt-2">
              {editingTagId && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEditingTagId(null);
                    setTagName("");
                    setTagSlug("");
                    setTagDesc("");
                  }}
                >
                  Cancel
                </Button>
              )}
              <Button type="submit" size="sm" className="bg-[#ff0000] hover:bg-[#e00000] text-white">
                <Plus className="h-4 w-4 mr-1" />
                {editingTagId ? "Update Tag" : "Add Tag"}
              </Button>
            </div>
          </form>

          {/* Tags List */}
          <div className="mt-6 border-t border-[#F1F5F9] pt-6">
            <h3 className="text-sm font-semibold text-[#1A1F2C] mb-3">Existing Tags</h3>
            {loadingTags ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 text-emerald-500 animate-spin" />
              </div>
            ) : tags.length === 0 ? (
              <p className="text-sm text-[#5A6A85] text-center py-6">No tags added yet.</p>
            ) : (
              <div className="space-y-2 max-h-[350px] overflow-y-auto pr-1">
                {tags.map((tag) => (
                  <div
                    key={tag.term_id}
                    className="flex items-center justify-between p-3 rounded-xl border border-[#E2E8F0] hover:border-emerald-100 bg-[#FAFCFA] transition-all"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm text-[#1A1F2C] truncate">
                          {tag.name}
                        </span>
                        {/* Apple-style Soft Lavender Badge */}
                        <span
                          style={{ backgroundColor: "#F4F2FF", color: "#1A1F2C" }}
                          className="text-[10px] font-bold px-2 py-0.5 rounded-full border border-[#E0DAFF]"
                        >
                          Lavender Tag
                        </span>
                      </div>
                      <p className="text-xs text-[#5A6A85] mt-1 truncate">
                        {tag.description || "No description."} (Slug: {tag.slug})
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 ml-4">
                      <span className="text-xs font-semibold text-[#5A6A85] bg-white border border-[#E2E8F0] px-2 py-0.5 rounded-md">
                        {tag.count} {tag.count === 1 ? "course" : "courses"}
                      </span>
                      <button
                        onClick={() => startEditTag(tag)}
                        className="p-1.5 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors cursor-pointer"
                        title="Edit"
                      >
                        <Edit className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => handleDeleteTerm(tag.term_id, "course_class_tag")}
                        className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                        title="Delete"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
